import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface AuthState {
    isAuthenticated: boolean;
    username: string | null;
    loginAttempts: number;
    lastAttemptTime?: number;
}

const initialState: AuthState = {
    isAuthenticated: false,
    username: null,
    loginAttempts: 0,
    lastAttemptTime: undefined,
};

// Check if user is authenticated in sessionStorage
const checkSessionAuth = (): { isAuthenticated: boolean; username: string | null } => {
    try {
        const authData = sessionStorage.getItem('macread_auth');
        if (authData) {
            const { authenticated, username } = JSON.parse(authData);
            // Check if authentication is still valid (session-based)
            return {
                isAuthenticated: authenticated === true,
                username: username || null,
            };
        }
    } catch (error) {}
    return { isAuthenticated: false, username: null };
};

// Save authentication status to sessionStorage
const saveAuthToSession = (isAuthenticated: boolean, username?: string): void => {
    try {
        const authData = {
            authenticated: isAuthenticated,
            username: username || null,
            timestamp: Date.now(),
        };
        sessionStorage.setItem('macread_auth', JSON.stringify(authData));
    } catch (error) {}
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        restoreFromSession: state => {
            const { isAuthenticated, username } = checkSessionAuth();
            state.isAuthenticated = isAuthenticated;
            state.username = username;
        },
        login: (state, action: PayloadAction<{ username: string; password: string }>) => {
            const { username, password } = action.payload;
            const correctPassword = 'Mackom2025!';

            if (password === correctPassword && username.trim().length > 0) {
                state.isAuthenticated = true;
                state.username = username.trim();
                state.loginAttempts = 0;
                state.lastAttemptTime = undefined;
                saveAuthToSession(true, username.trim());
            } else {
                state.loginAttempts += 1;
                state.lastAttemptTime = Date.now();
            }
        },
        logout: state => {
            state.isAuthenticated = false;
            state.username = null;
            state.loginAttempts = 0;
            state.lastAttemptTime = undefined;
            try {
                sessionStorage.removeItem('macread_auth');
            } catch {
                try {
                    sessionStorage.setItem('macread_auth', JSON.stringify({ authenticated: false }));
                } catch {
                    console.warn('[Auth] Could not clear session storage');
                }
            }
        },
        resetLoginAttempts: state => {
            state.loginAttempts = 0;
            state.lastAttemptTime = undefined;
        },
    },
});

export const { restoreFromSession, login, logout, resetLoginAttempts } = authSlice.actions;

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectUsername = (state: { auth: AuthState }) => state.auth.username;
export const selectLoginAttempts = (state: { auth: AuthState }) => state.auth.loginAttempts;

export default authSlice.reducer;
